import {type MouseEvent} from 'react';
import {Check, Link} from 'lucide-react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useCopy
} from '@kroll-resolver-crisp/ui-components-react';
import {cn} from '@/lib/utils';

// Platform URLs are shown and copied, never followed or embedded from inside the app. A click-through
// or an embedded player puts the analyst's browser in front of the platform straight from PRI, so the
// visit can be tied back to us; the analyst pastes the URL into whatever environment they review
// platform content from instead.

function urlHost(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

/**
An icon button that copies a platform URL, for a toolbar or overlay with no room for the URL itself.
Never lets the click reach a card or row underneath.
*/
export function CopyUrlButton({
  url,
  label = 'Copy URL',
  size = 'icon-xs',
  className,
  onCopied
}: {
  readonly url: string;
  readonly label?: string;
  readonly size?: 'icon-xs' | 'icon-sm';
  readonly className?: string;
  readonly onCopied?: () => void;
}) {
  const {copy, isCopied} = useCopy({value: url, ...(onCopied ? {onCopied} : {})});

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size={size}
            aria-label={label}
            className={className}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              void copy();
            }}
          />
        }>
        {isCopied ? <Check /> : <Link />}
      </TooltipTrigger>
      <TooltipContent>{isCopied ? 'Copied' : label}</TooltipContent>
    </Tooltip>
  );
}

/**
A platform URL as selectable text with a copy button beside it. Many are redirectors with long
tracking paths, so the host leads and can't be hidden by the path; the full URL is on hover.
*/
export function CopyableUrl({
  url,
  label = 'Copy URL',
  className,
  onCopied
}: {
  readonly url: string;
  readonly label?: string;
  readonly className?: string;
  readonly onCopied?: () => void;
}) {
  const host = urlHost(url);

  return (
    <span className={cn('inline-flex max-w-full min-w-0 items-center gap-1', className)}>
      <span title={url} className="truncate select-all">
        {host ? (
          <>
            <span className="font-medium">{host}</span>
            <span className="text-muted-foreground">
              {url.slice(url.indexOf(host) + host.length)}
            </span>
          </>
        ) : (
          url
        )}
      </span>
      <CopyUrlButton
        url={url}
        label={label}
        className="shrink-0"
        {...(onCopied ? {onCopied} : {})}
      />
    </span>
  );
}
